import { useReactiveVar } from "@apollo/client";
import { calculatePagerPageNumber } from "@flixbus-phx/marketplace-common";
import image from "@flixbus-phx/marketplace-common/assets/illustrations/landscape-eu-flixbus-flixtrain-s.svg";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import getNumberForPageSize from "../../../../shared/helpers/getNumberForPageSize/getNumberForPageSize";
import getRideFilterInput from "../../../../shared/helpers/getRideFilterInput/getRideFilterInput";
import { PageSize } from "../../../../shared/types/schema";
import rideUpdateErrorsVar from "../../../../state/rideUpdateErrors/reactiveVariable";
import { RideFilter } from "../../../../state/useRideFilter/useRideFilter";
import { RideSort } from "../../../../state/useRideSort/useRideSort";
import { useFilterRidesQuery } from "../../api/operations.generated";
import getRideUpdateErrorsPerRide from "../../helpers/getRideUpdateErrorsPerRide/getRideUpdateErrorsPerRide";
import RideItemInfoText from "../../ui/rideItemInfoText/RideItemInfoText";
import RideListPageSkeleton from "../../ui/rideListPageSkeleton/RideListPageSkeleton";
import { MemoizedRideTableRow } from "../rideTableRow/RideTableRow";
import * as css from "./RideListPage.scss";

type Props = {
  rideFilter: RideFilter;
  rideSort: RideSort;
  page: number;
};

const RideListPage: React.FC<Props> = ({ rideFilter, rideSort, page, children }) => {
  const rideUpdateErrors = useReactiveVar(rideUpdateErrorsVar);

  const { data, loading, error } = useFilterRidesQuery({
    variables: {
      pageRequest: {
        page,
        pageSize: PageSize.Fifty,
      },
      rideFilter: getRideFilterInput(rideFilter, false, false),
      sort: rideSort,
    },
    pollInterval: 1000 * 30,
    fetchPolicy: "cache-first",
  });

  if (loading) {
    return (
      <>
        <RideListPageSkeleton />
        <RideListPageSkeleton />
        <RideListPageSkeleton />
        <RideListPageSkeleton />
        <RideListPageSkeleton />
      </>
    );
  }

  if (error && page === 0) {
    return <RideItemInfoText isError />;
  }

  if (data?.filterRides) {
    if (data.filterRides.items.length > 0) {
      const isLastPage =
        page === calculatePagerPageNumber(data.filterRides.total, getNumberForPageSize(PageSize.Fifty)) - 1;

      return (
        <>
          {data.filterRides.items.map((ride) => {
            return (
              <MemoizedRideTableRow
                key={ride.id}
                ride={ride}
                rideUpdateErrors={getRideUpdateErrorsPerRide(rideUpdateErrors, ride.id)}
              />
            );
          })}
          {isLastPage ? (
            <div className={css.endOfListWrapper}>
              <FormattedMessage id="ride.list.end" />
              <img src={image} alt="Flixbus and Flixtrain in city" />
            </div>
          ) : (
            children
          )}
        </>
      );
    }
    return <RideItemInfoText />;
  }

  return <></>;
};

export default RideListPage;
